import React, { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * HorizontalScroller — a flex row that scrolls horizontally with:
 *   • Native touch / drag on mobile
 *   • Chevron buttons (visible on hover) on desktop, only shown when needed
 *   • Hidden scrollbar (for visual cleanliness)
 *   • Mouse-wheel-to-horizontal-scroll for desktop trackpads / mice
 *
 * Drop-in replacement for `<div className="flex overflow-x-auto ...">`.
 *
 * Usage:
 *   <HorizontalScroller className="gap-4 pb-2">
 *     {items.map(item => <Card key={item.id} {...item} />)}
 *   </HorizontalScroller>
 */
export const HorizontalScroller = ({ children, className = '', testId, arrowOffsetClass = '-translate-y-1/2 top-1/2' }) => {
  const ref = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    update();
    const el = ref.current;
    if (!el) return;
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update, children]);

  const scrollByAmount = (dir) => {
    const el = ref.current;
    if (!el) return;
    const amount = Math.max(el.clientWidth * 0.8, 240);
    el.scrollBy({ left: dir * amount, behavior: 'smooth' });
  };

  // Convert vertical wheel events to horizontal scrolling on desktop (helps
  // users with a regular mouse who don't know shift+wheel).
  const onWheel = (e) => {
    const el = ref.current;
    if (!el) return;
    // Only intercept when the user is producing vertical wheel input AND there
    // is horizontal room to scroll. Trackpad two-finger horizontal gestures
    // already produce e.deltaX and we should leave them alone.
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX) && (canLeft || canRight)) {
      // Only block default vertical scroll if there's room to scroll horizontally.
      const goingRight = e.deltaY > 0;
      if ((goingRight && canRight) || (!goingRight && canLeft)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    }
  };

  return (
    <div className="relative group">
      <div
        ref={ref}
        onWheel={onWheel}
        className={`flex overflow-x-auto scrollbar-hide snap-x ${className}`}
        style={{ scrollbarWidth: 'none' }}
        data-testid={testId}
      >
        {children}
      </div>
      {canLeft && (
        <button
          type="button"
          onClick={() => scrollByAmount(-1)}
          aria-label="Scroll left"
          data-testid={testId ? `${testId}-prev` : 'h-scroll-prev'}
          className={`hidden md:flex items-center justify-center absolute left-2 ${arrowOffsetClass} z-10 w-9 h-9 rounded-full bg-white shadow-md border border-slate-200 text-[#0A2C93] hover:bg-[#0A2C93] hover:text-white transition-colors opacity-0 group-hover:opacity-100`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {canRight && (
        <button
          type="button"
          onClick={() => scrollByAmount(1)}
          aria-label="Scroll right"
          data-testid={testId ? `${testId}-next` : 'h-scroll-next'}
          className={`hidden md:flex items-center justify-center absolute right-2 ${arrowOffsetClass} z-10 w-9 h-9 rounded-full bg-white shadow-md border border-slate-200 text-[#0A2C93] hover:bg-[#0A2C93] hover:text-white transition-colors opacity-0 group-hover:opacity-100`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default HorizontalScroller;
